import {
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useMyProfile } from "@/entities/user";
import { useSiteSettingDetails } from "@/entities/store";
import { hasPermission } from "@/features/auth";
import {
  CurrencyContext,
  type CurrencyContextType,
  KEY_ACTIVE_STORE,
  currencyKey,
  languageKey,
} from "@/shared/lib/currency";

const isBrowser = typeof window !== "undefined";

/**
 * Resolve a store's display currency. Prefer the store's own `currency`, but
 * fall back to its country's `currencyCode` (the BE now embeds the full country
 * record on the store) so a store that never had an explicit currency set —
 * e.g. a US store — resolves to USD instead of the NPR default. Mirrors the
 * fallback the Store Settings page uses.
 */
function resolveStoreCurrency(store: {
  currency?: string;
  currencyCode?: string;
  country?: string | { currencyCode?: string } | null;
}): string | undefined {
  if (store.currency) return store.currency;
  if (store.country && typeof store.country === "object" && store.country.currencyCode) {
    return store.country.currencyCode;
  }
  if (store.currencyCode) return store.currencyCode;
  return undefined;
}

function lsGet(key: string): string | null {
  if (!isBrowser) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  if (!isBrowser) return;
  try {
    localStorage.setItem(key, value);
  } catch {
    // silently ignore (e.g. private browsing quota)
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [storeId, setStoreId] = useState<string | null>(null);
  const [currency, setCurrencyState] = useState<string>("NPR");
  const [language, setLanguageState] = useState<string>("en");
  const [isHydrated, setIsHydrated] = useState(false);

  const profile = useMyProfile();
  const isSyncing = profile.isLoading || profile.isFetching;
  // `/auth/profile` returns a minimal store (no currency/country), so the
  // profile alone can't tell a USD store from the NPR default. `/stores/{id}`
  // embeds the country + currency (the same record Store Settings reads), so
  // consume it here to resolve the real currency. MUST be gated on auth:
  // KEY_ACTIVE_STORE survives logout, and this provider mounts on /login too —
  // an ungated fetch there goes out tokenless, 401s, and the interceptor's
  // redirect turns the login page into an infinite reload loop.
  const { isAuthenticated } = useAuth0();
  // Fetch ONLY with the id the current session's profile reports — never the
  // localStorage id. KEY_ACTIVE_STORE survives logout, so after logging into
  // a different account the cached id belongs to someone else's store and
  // GET /stores/{staleId} comes back 403. localStorage stays display-only
  // (instant currency before the profile lands).
  const profileStore =
    (profile.data as any)?.data?.store ?? (profile.data as any)?.store;
  const profileStoreId: string | undefined = profileStore?.id ?? undefined;
  // GET /stores/{id} needs store.read. A member without it got a 403 on every
  // page (14 Oct) and gained nothing: the profile already carries the store's
  // currency (Phase 2), which is what they fall back to. This provider sits
  // above PermissionProvider, hence the plain check on the profile.
  const profileData = (profile.data as any)?.data ?? null;
  const canReadStore = !!profileData?.user && hasPermission(profileData, "store", "read");
  const storeDetails = useSiteSettingDetails(
    isAuthenticated && profileStoreId && canReadStore ? profileStoreId : undefined,
  );

  // Phase 1: localStorage hydration on mount
  useEffect(() => {
    const id = lsGet(KEY_ACTIVE_STORE);
    setStoreId(id);
    if (id) {
      const c = lsGet(currencyKey(id));
      const l = lsGet(languageKey(id));
      if (c) setCurrencyState(c);
      if (l) setLanguageState(l);
    }
    setIsHydrated(true);
  }, []);

  // Phase 2: profile reconciliation — no new API call, consumes React Query cache
  useEffect(() => {
    const store =
      (profile.data as any)?.data?.store ?? (profile.data as any)?.store;
    if (!store || !isBrowser) return;

    const { id, language: pl } = store;
    // Resolve currency with the country fallback so US/India stores whose
    // `currency` field isn't explicitly set still reflect their real currency
    // instead of defaulting to NPR.
    const pc = resolveStoreCurrency(store);

    if (id) {
      const stored = lsGet(KEY_ACTIVE_STORE);
      if (stored !== id) {
        lsSet(KEY_ACTIVE_STORE, id);
        setStoreId(id);
      }
      // Use the reconciled storeId for currency/language keys
      if (pc) {
        const cached = lsGet(currencyKey(id));
        if (cached !== pc) {
          lsSet(currencyKey(id), pc);
          setCurrencyState(pc);
        }
      }
      if (pl) {
        const cached = lsGet(languageKey(id));
        if (cached !== pl) {
          lsSet(languageKey(id), pl);
          setLanguageState(pl);
        }
      }
    }
  }, [profile.data]);

  // Phase 2b: authoritative currency from the store-details endpoint. This is
  // the store record Store Settings reads (country embedded), so a US store
  // resolves to USD instead of falling back to the NPR default that
  // `/auth/profile` (Phase 2) can't override. Runs whenever the details load
  // or the active store changes.
  useEffect(() => {
    const store = (storeDetails.data as any)?.data ?? (storeDetails.data as any);
    // Key by the PROFILE's store id (what the fetch above used), not the
    // hydrated localStorage id — during an account switch the latter can
    // still be the previous account's store for a render or two.
    if (!store || !profileStoreId || !isBrowser) return;
    const pc = resolveStoreCurrency(store);
    if (!pc) return;
    const cached = lsGet(currencyKey(profileStoreId));
    if (cached !== pc) {
      lsSet(currencyKey(profileStoreId), pc);
      setCurrencyState(pc);
    }
  }, [storeDetails.data, profileStoreId]);

  // Phase 3: multi-store tab sync via storage events
  useEffect(() => {
    if (!isBrowser) return;
    const handler = (e: StorageEvent) => {
      if (e.key === KEY_ACTIVE_STORE && e.newValue) {
        const newId = e.newValue;
        setStoreId(newId);
        const c = lsGet(currencyKey(newId));
        const l = lsGet(languageKey(newId));
        setCurrencyState(c ?? "NPR");
        setLanguageState(l ?? "en");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setCurrency = useCallback(
    (next: string) => {
      setCurrencyState(next);
      const id = storeId ?? lsGet(KEY_ACTIVE_STORE);
      if (id) lsSet(currencyKey(id), next);
    },
    [storeId],
  );

  const setLanguage = useCallback(
    (next: string) => {
      setLanguageState(next);
      const id = storeId ?? lsGet(KEY_ACTIVE_STORE);
      if (id) lsSet(languageKey(id), next);
    },
    [storeId],
  );

  const value: CurrencyContextType = {
    storeId,
    currency,
    language,
    isHydrated,
    isSyncing,
    setCurrency,
    setLanguage,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}
